/**
 * Shared API client for the WandererTrip backend.
 * Auto-attaches Bearer token from localStorage and handles token refresh.
 * No React context needed -- works from any service file.
 */

import { isTokenExpired, refreshAccessToken, parseJwt } from '../utils/oauth';
import { apiBaseUrl } from '../config/auth';

/** Prefer env/config; fall back to production API. */
export const API_BASE_URL =
  (apiBaseUrl && apiBaseUrl.replace(/\/$/, '')) || 'https://api.wanderertrip.com';
const JSONTRANSFER_URL = `${API_BASE_URL}/jsontransfer`;

const ACCESS_TOKEN_KEY = 'auth_access_token';
const ID_TOKEN_KEY = 'auth_id_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';

/** API envelope format used by the jsontransfer endpoint */
export interface ApiEnvelope<T = unknown> {
  id: string;
  type: string;
  body: T;
}

/**
 * Get a valid access token, refreshing if needed.
 * Returns null if no token is available or refresh fails.
 */
export async function getValidToken(): Promise<string | null> {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!accessToken) return null;

  if (!isTokenExpired(accessToken)) {
    return accessToken;
  }

  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken || isTokenExpired(refreshToken)) {
    return null;
  }

  try {
    const response = await refreshAccessToken(refreshToken);
    localStorage.setItem(ACCESS_TOKEN_KEY, response.access_token);
    localStorage.setItem(ID_TOKEN_KEY, response.id_token);
    if (response.refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token);
    }
    return response.access_token;
  } catch (err) {
    console.error('apiClient: token refresh failed', err);
    return null;
  }
}

/** Build headers with auth and content type */
export async function buildAuthHeaders(includeContentType = false): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    Accept: 'application/json, application/problem+json',
  };
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  const token = await getValidToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/**
 * GET /jsontransfer?id={id}
 * Returns the parsed body from the envelope, or null if not found.
 */
export async function apiGet<T = unknown>(id: string): Promise<T | null> {
  const headers = await buildAuthHeaders();
  const url = `${JSONTRANSFER_URL}?id=${encodeURIComponent(id)}`;

  const response = await fetch(url, { method: 'GET', headers });

  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error(`apiGet failed: ${response.status}`);
  }

  const envelope: ApiEnvelope<T> = await response.json();
  return envelope.body;
}

/**
 * PUT /jsontransfer
 * Sends { id, type, body } envelope.
 */
export async function apiPut<T = unknown>(id: string, type: string, body: T): Promise<void> {
  const headers = await buildAuthHeaders(true);
  const payload: ApiEnvelope<T> = { id, type, body };

  const response = await fetch(JSONTRANSFER_URL, {
    method: 'PUT',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let detail = `${response.status}`;
    try {
      const err = await response.json();
      if (err?.detail) detail = String(err.detail);
      else if (err?.title) detail = String(err.title);
    } catch { /* ignore parse errors */ }
    throw new Error(`apiPut failed: ${detail}`);
  }
}

/**
 * DELETE /jsontransfer?id={id}
 * Returns true if deleted (204), false otherwise.
 */
export async function apiDelete(id: string): Promise<boolean> {
  const headers = await buildAuthHeaders();
  const url = `${JSONTRANSFER_URL}?id=${encodeURIComponent(id)}`;

  const response = await fetch(url, { method: 'DELETE', headers });

  if (!response.ok && response.status !== 204) {
    throw new Error(`apiDelete failed: ${response.status}`);
  }

  return true;
}

/**
 * Helper: get user email from the stored ID token.
 * Useful for building backend keys without React context.
 */
export function getUserEmailFromToken(): string | null {
  const idToken = localStorage.getItem(ID_TOKEN_KEY);
  if (!idToken) return null;
  const parsed = parseJwt(idToken);
  return parsed?.email ?? null;
}

/** Cognito `sub` from ID token (preferred) or access token. */
export function getUserSubFromToken(): string | null {
  const idToken = localStorage.getItem(ID_TOKEN_KEY);
  if (idToken) {
    const parsed = parseJwt(idToken);
    if (parsed?.sub) return String(parsed.sub);
  }
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (accessToken) {
    const parsed = parseJwt(accessToken);
    if (parsed?.sub) return String(parsed.sub);
  }
  return null;
}
